import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ShieldCheck, Star, Car, PawPrint, Clock, MapPin, CheckCircle2, Heart } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { FloatingContact } from "../components/FloatingContact";
import { Marquee } from "../components/Marquee";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { QuoteLink } from "../components/QuoteLink";
import { api, getSettings, FALLBACK_SETTINGS, imgUrl } from "../api";
import { useLang, UI, FAQS } from "../i18n";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const IMG_PARIS = "https://images.unsplash.com/photo-1511739001486-6bfe10ce785f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjA1MDV8MHwxfHNlYXJjaHwyfHxlaWZmZWwlMjB0b3dlciUyMFBhcmlzJTIwdHJhdmVsJTIwbHV4dXJ5fGVufDB8fHx8MTc5MTc3MTU1Nnww&ixlib=rb-4.1.0&q=60&w=1200&auto=format";
const IMG_LONDON = "https://images.unsplash.com/photo-1745016176874-cd3ed3f5bfc6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w6NjY2NjV8MHwxfHNlYXJjaHwzfHxiaWclMjBiZW4lMjBMb25kb24lMjBza3lsaW5lJTIwc3Vuc2V0fGVufDB8fHx8MTc5MTc3MTU2M3ww&ixlib=rb-4.1.0&q=60&w=1200&auto=format";
const IMG_GOLDEN = "https://images.unsplash.com/photo-1621273623907-b39f17cc70eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxnb2xkZW4lMjByZXRyaWV2ZXIlMjBsdXh1cnklMjBjYXIlMjB0cmF2ZWxpbmclMjBwYXJpc3xlbnwwfHx8fDE3OTE3NzE1NTZ8MA&ixlib=rb-4.1.0&q=60&w=1400&auto=format";
const IMG_CAT = "/images/hero-pets.webp";
const IMG_CAT_MOBILE = "/images/hero-pets-mobile.webp";

export default function Home() {
  const [routes, setRoutes] = useState([]);
  const [posts, setPosts] = useState([]);
  const [rates, setRates] = useState(FALLBACK_SETTINGS.rates);
  const { lang, prefix } = useLang();
  const s = UI[lang];
  const faqs = FAQS[lang];
  const fr = lang === "fr";

  useEffect(() => {
    api.get("/routes", { params: { lang } }).then((r) => setRoutes(r.data)).catch(() => {});
    api.get("/blog", { params: { lang } }).then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
    getSettings().then((st) => setRates(st.rates));
  }, [lang]);

  const seoTitle = fr
    ? "Taxi Animalier Londres ↔ Europe | Porte à Porte"
    : "Pet Taxi London ↔ Europe | Door-to-Door Pet Transport";
  const seoDesc = fr
    ? "Taxi animalier porte à porte entre Londres et l'Europe via Eurotunnel. Vous voyagez avec votre animal. Agréé DEFRA Type 2. Devis fixes."
    : "Door-to-door pet taxi between London and Europe via Eurotunnel. You travel with your pet. DEFRA Type 2 authorised. Fixed quotes.";

  const steps = [
    { n: "01", t: s.step1_t, d: s.step1_d },
    { n: "02", t: s.step2_t, d: s.step2_d },
    { n: "03", t: s.step3_t, d: s.step3_d },
    { n: "04", t: s.step4_t, d: s.step4_d },
  ];

  return (
    <div className="min-h-screen bg-champagne">
      <Seo title={seoTitle} description={seoDesc} path="/" />
      <Helmet>
        <link rel="preload" as="image" href="/images/hero-pets-mobile.webp" media="(max-width: 639px)" fetchPriority="high" />
        <link rel="preload" as="image" href="/images/hero-pets.webp" media="(min-width: 640px)" fetchPriority="high" />
      </Helmet>
      <Header />

      {/* HERO - full-bleed tinted cat background, mirrors UK site style */}
      <section className="relative overflow-hidden bg-[#0B0F0C]" data-testid="hero-section">
        <div className="absolute inset-0 hidden sm:block">
          <img
            src={IMG_CAT}
            alt={fr ? "Chiot golden retriever et chat tigré dormant ensemble - Pet Taxi London, taxi animalier Londres Europe" : "Golden retriever puppy and tabby cat sleeping together - Pet Taxi London, door-to-door pet taxi London Europe"}
            className="absolute inset-0 w-full h-full object-cover object-[center_60%]"
            fetchPriority="high"
            data-testid="hero-cat-image"
          />
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/45" />
        </div>
        <div className="absolute inset-0 sm:hidden">
          <img
            src={IMG_CAT_MOBILE}
            alt={fr ? "Chiot golden retriever et chat tigré dormant ensemble - Pet Taxi London" : "Golden retriever puppy and tabby cat sleeping together - Pet Taxi London"}
            className="absolute inset-0 w-full h-full object-cover"
            fetchPriority="high"
            data-testid="hero-cat-image-mobile"
          />
          <div className="absolute inset-0 bg-black/30" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/10 to-black/50" />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-8 min-h-[100svh] sm:min-h-[92vh] flex flex-col items-center justify-center text-center pt-24 pb-16">
          <div
            className="animate-hero inline-flex items-center gap-2 rounded-full bg-black/40 border border-white/15 backdrop-blur px-5 py-2"
            style={{ animationDelay: "0.1s" }}
            data-testid="hero-eyebrow"
          >
            <PawPrint className="w-4 h-4 text-white/80" />
            <span className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.15em] sm:tracking-[0.2em] text-white/90 whitespace-nowrap">
              {fr ? "Londres ↔ Europe · Porte-à-porte" : "London ↔ Europe · Door-to-door"}
            </span>
          </div>

          <h1
            className="animate-hero mt-7 font-sans font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.1]"
            style={{ animationDelay: "0.25s" }}
            data-testid="hero-heading"
          >
            {fr ? "Taxi animalier entre Londres et l'Europe." : "Pet taxi between London and Europe."}
          </h1>

          <p
            className="animate-hero mt-6 text-base sm:text-lg text-white/85 leading-relaxed max-w-xl"
            style={{ animationDelay: "0.4s" }}
          >
            {fr
              ? "Service de chauffeur animalier — vous et votre animal voyagez ensemble, porte à porte via Eurotunnel. Départs de toutes les gares de Londres, arrivées CDG et au-delà. Agréé DEFRA. Chiens, chats & furets."
              : "Personal pet chauffeur service — you and your pet travel together, door-to-door via Eurotunnel. Pickups at every London station, drop-offs at CDG and beyond. DEFRA approved. Dogs, cats & ferrets."}
          </p>

          <div
            className="animate-hero mt-9 w-full max-w-sm space-y-4"
            style={{ animationDelay: "0.55s" }}
          >
            <QuoteLink
              testid="hero-instant-quote-btn"
              className="group flex items-center justify-center gap-2 w-full rounded-xl bg-[#C3D9B6] text-[#14210F] font-bold px-6 py-2.5 text-sm sm:px-8 sm:py-4 sm:text-lg hover:bg-[#B2CCa3] transition-colors"
            >
              {fr ? "Demander un devis" : "Request a quote"}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </QuoteLink>
            <a
              href={`https://wa.me/${FALLBACK_SETTINGS.contact.whatsapp}?text=${encodeURIComponent(fr ? "Bonjour ! Je souhaite un devis pour un transport d'animal entre Londres et l'Europe." : "Hi! I'd like a quote for pet transport between London and Europe.")}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-link"
              className="block w-full rounded-xl border border-[#25D366]/40 bg-[#25D366]/10 backdrop-blur text-white font-semibold px-6 py-2.5 text-sm sm:px-8 sm:py-4 sm:text-lg hover:border-[#25D366]/70 transition-colors"
            >
              {fr ? "WhatsApp — chat en direct" : "WhatsApp — live chat"}
            </a>
          </div>

          <div
            className="animate-hero mt-10 flex flex-wrap justify-center gap-x-7 gap-y-3 text-white/85 text-sm"
            style={{ animationDelay: "0.7s" }}
            data-testid="hero-trust-badges"
          >
            <span className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-white/90" /> {fr ? "Agréé DEFRA" : "DEFRA Approved"}</span>
            <span className="flex items-center gap-2"><Heart className="w-5 h-5 text-white/90" /> {fr ? "Vous voyagez avec votre animal" : "Owner travels with pet"}</span>
            <span className="flex items-center gap-2"><MapPin className="w-5 h-5 text-white/90" /> {fr ? "Porte-à-porte" : "Door-to-door"}</span>
          </div>
        </div>
      </section>

      <Marquee />

      {/* SERVICES BENTO */}
      <section id="services" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24" data-testid="services-section">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold-dark mb-4">{s.services_eyebrow}</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-ink max-w-2xl">
            {s.services_h2}
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          <Reveal className="md:col-span-2">
            <div className="h-full bg-[#0B0F0C] text-champagne rounded-3xl p-8 sm:p-12 relative overflow-hidden">
              <PawPrint className="w-8 h-8 text-gold mb-6" />
              <h3 className="font-serif text-2xl sm:text-3xl font-medium">{s.card1_t}</h3>
              <p className="mt-4 text-champagne/70 leading-relaxed max-w-md">{s.card1_b}</p>
              <div className="mt-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-gold">
                <MapPin className="w-4 h-4" /> {fr ? "Paris · Calais · Folkestone · Londres" : "Paris · Calais · Folkestone · London"}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full bg-gold-light border border-gold-border rounded-3xl p-8">
              <ShieldCheck className="w-8 h-8 text-gold-dark mb-6" />
              <h3 className="font-serif text-xl sm:text-2xl font-medium text-ink">{s.card2_t}</h3>
              <p className="mt-3 text-sm text-zinc-600 leading-relaxed">{s.card2_b}</p>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="h-full bg-white border border-zinc-200 rounded-3xl p-8">
              <Car className="w-8 h-8 text-gold-dark mb-6" />
              <h3 className="font-serif text-xl sm:text-2xl font-medium text-ink">{s.card3_t}</h3>
              <p className="mt-3 text-sm text-zinc-600 leading-relaxed">{s.card3_b}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full bg-white border border-zinc-200 rounded-3xl p-8">
              <CheckCircle2 className="w-8 h-8 text-gold-dark mb-6" />
              <h3 className="font-serif text-xl sm:text-2xl font-medium text-ink">{s.card4_t}</h3>
              <p className="mt-3 text-sm text-zinc-600 leading-relaxed">{s.card4_b}</p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="h-full bg-white border border-zinc-200 rounded-3xl p-8">
              <Clock className="w-8 h-8 text-gold-dark mb-6" />
              <h3 className="font-serif text-xl sm:text-2xl font-medium text-ink">{s.card5_t}</h3>
              <p className="mt-3 text-sm text-zinc-600 leading-relaxed">{s.card5_b}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TWO CITIES STRIP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-24">
        <div className="grid md:grid-cols-2 gap-6">
          {[
            { img: IMG_PARIS, label: "Paris", sub: s.city_paris_sub },
            { img: IMG_LONDON, label: s.city_london, sub: s.city_london_sub },
          ].map((c, i) => (
            <Reveal key={c.label} delay={i * 0.1}>
              <div className="group relative rounded-3xl overflow-hidden aspect-[16/9]">
                <img
                  src={c.img}
                  alt={fr ? `${c.label} - ville de transport d'animaux` : `${c.label} - pet transport city`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-midnight/80 to-transparent" />
                <div className="absolute bottom-6 left-6">
                  <p className="font-serif text-2xl text-champagne">{c.label}</p>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold mt-1">{c.sub}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ROUTES */}
      <section className="bg-cream py-24" data-testid="routes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold-dark mb-4">{s.routes_eyebrow}</p>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-ink">
                {s.routes_h2}
              </h2>
            </div>
            <Link to={`${prefix}/routes`} className="text-sm font-semibold text-gold-dark hover:text-ink transition-colors flex items-center gap-1.5">
              {s.routes_all} <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {routes.slice(0, 6).map((r, i) => (
              <Reveal key={r.slug} delay={i * 0.06}>
                <div
                  className="group relative bg-white border border-zinc-200 rounded-3xl p-7 hover:border-gold hover:shadow-lg transition-all h-full"
                >
                  <div className="flex items-center justify-between">
                    <PawPrint className="w-5 h-5 text-gold" />
                    <span className="font-mono text-xs text-zinc-500">{r.distance_miles} mi · {r.duration}</span>
                  </div>
                  <p className="mt-5 font-serif text-xl font-medium text-ink group-hover:text-gold-dark transition-colors">
                    <Link to={`${prefix}/routes/${r.slug}`} data-testid={`route-card-${r.slug}`} className="after:absolute after:inset-0 after:content-[''] focus:outline-none">{r.name}</Link>
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gold-dark">
                    {s.route_card_cta} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BLOG */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24" data-testid="blog-section">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold-dark mb-4">{fr ? "Le blog" : "The blog"}</p>
            <h2 className="font-sans text-3xl sm:text-4xl font-bold tracking-tight text-ink max-w-xl">
              {fr ? "Guides et conseils pour voyager avec votre animal" : "Guides & tips for travelling with your pet"}
            </h2>
          </div>
          <Link to={`${prefix}/blog`} data-testid="home-blog-link" className="text-sm font-semibold text-gold-dark hover:text-ink transition-colors flex items-center gap-1.5">
            {fr ? "Tous les articles" : "All articles"} <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {posts.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.06}>
              <div
                className="group relative bg-white border border-zinc-200 rounded-3xl overflow-hidden hover:border-gold hover:shadow-lg transition-all h-full"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={imgUrl(p.image, 800)} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-gold-dark">{p.category}</p>
                  <p className="mt-2 font-sans text-lg font-bold text-ink leading-snug group-hover:text-gold-dark transition-colors">
                    <Link to={`${prefix}/blog/${p.slug}`} data-testid={`home-blog-card-${p.slug}`} className="after:absolute after:inset-0 after:content-[''] focus:outline-none">{p.title}</Link>
                  </p>
                  <p className="mt-2 text-sm text-zinc-600 leading-relaxed line-clamp-2">{p.excerpt}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24" data-testid="reviews-section">
        <Reveal className="text-center mb-12">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-midnight/50">{fr ? "Avis" : "Reviews"}</p>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-ink">
            {fr ? "Ce que disent les propriétaires" : "What owners say"}
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              text: fr
                ? "Notre golden retriever a dormi tout le trajet, de notre porte à Islington à l'appartement de mes parents dans le 15e. Le chauffeur a envoyé des photos au tunnel. Chaque centime en valait la peine."
                : "Our golden retriever slept the whole way from our door in Islington to my parents' flat in the 15th. The driver sent photo updates at the tunnel. Worth every penny.",
              name: "Sarah M.",
              route: fr ? "Londres → Paris" : "London to Paris",
            },
            {
              text: fr
                ? "Deux chats, et une propriétaire qui a le mal de mer. L'enregistrement à Coquelles a pris dix minutes et les garçons ne m'ont jamais quittée. Tellement plus doux que l'avion."
                : "Two cats, one ferry-phobic owner. The pet check-in at Coquelles took ten minutes and the boys never left my side. So much kinder than flying.",
              name: "Julien R.",
              route: fr ? "Paris → Londres" : "Paris to London",
            },
            {
              text: fr
                ? "Ils nous ont retrouvés à St Pancras à la descente du train d'Édimbourg et nous étions à Bruxelles pour le dîner. Impeccable, serein, et le devis fixe n'a jamais bougé."
                : "They met us at St Pancras off the Edinburgh train and had us in Brussels by dinner. Flawless, calm, and the fixed quote never moved.",
              name: "Emma & Tom W.",
              route: fr ? "Londres → Bruxelles" : "London to Brussels",
            },
          ].map((r, i) => (
            <Reveal key={i} delay={i * 0.12}>
              <div className="h-full bg-[#0B0F0C] rounded-3xl p-8 noise-overlay relative" data-testid={`review-card-${i}`}>
                <div className="flex items-center gap-1" role="img" aria-label="5 out of 5 stars">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 text-gold" fill="currentColor" />
                  ))}
                </div>
                <p className="mt-5 text-champagne/85 leading-relaxed text-sm sm:text-base">"{r.text}"</p>
                <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-[#A9C29A]">{r.name} · {r.route}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* JOURNEY STEPS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-24">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold-dark mb-4">{s.steps_eyebrow}</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-ink">
            {s.steps_h2}
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-10 md:grid-cols-4">
          {steps.map((st, i) => (
            <Reveal key={st.n} delay={i * 0.08}>
              <div className="border-t-2 border-gold/40 pt-6">
                <span className="font-mono text-sm text-gold-dark">{st.n}</span>
                <h3 className="mt-3 font-serif text-lg font-medium text-ink">{st.t}</h3>
                <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{st.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-[#0B0F0C] py-24 noise-overlay relative" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-4">{s.faq_eyebrow}</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-champagne">
              {s.faq_h2}
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="bg-champagne/5 border border-champagne/10 rounded-2xl px-6"
                >
                  <AccordionTrigger
                    className="text-left text-champagne font-medium hover:text-gold hover:no-underline py-5"
                    data-testid={`faq-question-${i}`}
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-champagne/70 leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="relative py-24 overflow-hidden">
        <img src={IMG_GOLDEN} alt={fr ? "Golden retriever voyageant confortablement dans un taxi animalier" : "Golden retriever travelling comfortably in a pet taxi"} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0B0F0C]/75" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-champagne leading-tight">
              {s.cta_h2}
            </h2>
            <p className="mt-5 text-champagne/70 max-w-xl mx-auto leading-relaxed">{s.cta_sub}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <QuoteLink
                testid="cta-quote-btn"
                className="rounded-full bg-gold text-white font-semibold px-8 py-4 text-sm hover:bg-gold-dark transition-colors"
              >
                {s.cta_btn1}
              </QuoteLink>
              <a
                href="https://wa.me/447892807912?text=Hi!%20I%20need%20pet%20transport%20between%20Paris%20and%20London."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-whatsapp-btn"
                className="rounded-full border border-champagne/40 text-champagne font-semibold px-8 py-4 text-sm hover:border-gold hover:text-gold transition-colors"
              >
                {s.cta_btn2}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
      <FloatingContact />
    </div>
  );
}
